import { doc, updateDoc } from "firebase/firestore";
import { deleteObject, getDownloadURL, getStorage, ref, uploadBytesResumable } from "firebase/storage";
import React, { useState } from "react";
import { Button } from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { db } from "../../firebase";
import DeleteButtonFile from "../Buttons/DeleteButtonFile";
import TopTitleFormComponent from "../forms/TopTitleFormComponent";
import { showCustomToast } from "../Misc/CustomToast";
import "./UpdloaderRoi.css";

const maxSizePicFile = 100 * 1024 * 1024;

function getFileKey(file) { return `${file.name}-${file.lastModified}-${file.size}`; }
function formatFileSize(bytes) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), 3);
  return `${(bytes / 1024 ** index).toFixed(index ? 1 : 0)} ${units[index]}`;
}

function UpdloaderRoi(props) {
  const [isLoading, setIsLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [fileLinks, setFileLinks] = useState(props.fileUrlsRoi || []);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadStatus, setUploadStatus] = useState({});

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length > 5) {
      showCustomToast("You can only upload up to 5 files");
      return;
    }
    setFiles(selectedFiles);
    setUploadProgress({});
    setUploadStatus(Object.fromEntries(selectedFiles.map((file) => [getFileKey(file), "queued"])));
  };

  const deleteFile = async (fileUrl, fileName, clientId) => {
    try {
      await deleteObject(ref(getStorage(), `Documents/ScannedRois/${clientId}/${fileName}`));
      const newFileLinks = fileLinks.filter((file) => file.url !== fileUrl);
      setFileLinks(newFileLinks);
      await updateDoc(doc(db, "clients", clientId), {
        fileUrlsRoi: newFileLinks.map((file) => ({ url: file.url, name: file.name })),
      });
      showCustomToast("File successfully deleted!", "success");
    } catch (error) {
      showCustomToast("Error deleting file", "error");
    }
  };

  const uploadFile = (docId, file) => new Promise((resolve, reject) => {
    const fileKey = getFileKey(file);
    const uploadTask = uploadBytesResumable(ref(getStorage(), `Documents/ScannedRois/${docId}/${file.name}`), file);
    uploadTask.on("state_changed", (snapshot) => {
      const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
      setUploadProgress((current) => ({ ...current, [fileKey]: progress }));
      setUploadStatus((current) => ({ ...current, [fileKey]: "uploading" }));
    }, (error) => {
      setUploadStatus((current) => ({ ...current, [fileKey]: "error" }));
      reject(error);
    }, async () => {
      try {
        const fileUrl = await getDownloadURL(uploadTask.snapshot.ref);
        setUploadProgress((current) => ({ ...current, [fileKey]: 100 }));
        setUploadStatus((current) => ({ ...current, [fileKey]: "complete" }));
        resolve({ url: fileUrl, name: file.name });
      } catch (error) {
        setUploadStatus((current) => ({ ...current, [fileKey]: "error" }));
        reject(error);
      }
    });
  });

  const uploadFilesAndGetUrls = async (docId) => {
    const fileUrls = [];
    for (const file of files) {
      if (file.size > maxSizePicFile) {
        showCustomToast(`File size exceeds the limit: ${file.name}`, "error");
        setUploadStatus((current) => ({ ...current, [getFileKey(file)]: "error" }));
        continue;
      }
      fileUrls.push(await uploadFile(docId, file));
    }
    return fileUrls;
  };

  const updateClientData = async (clientId) => {
    if (!files.length) {
      showCustomToast("No files selected for upload.", "error");
      return;
    }
    setIsLoading(true);
    try {
      const clientRef = doc(db, "clients", clientId);
      const uploadedFiles = await uploadFilesAndGetUrls(clientRef.id);
      const mergedFileUrls = [...fileLinks, ...uploadedFiles];
      await updateDoc(clientRef, { fileUrlsRoi: mergedFileUrls });
      setFileLinks(mergedFileUrls);
      showCustomToast("Files successfully uploaded!", "success");
    } catch (error) {
      console.error("Error updating client: ", error);
      showCustomToast(`Error updating client: ${error}`, "error");
    } finally { setIsLoading(false); }
  };

  const clientName = [props.firstName, props.lastName].filter(Boolean).join(" ");

  return (
    <div className="uploader-roi uploader-roi-modern">
      <TopTitleFormComponent closemodal={props.closemodal} eyebrow="Documents" title="Upload scanned ROI" subtitle={clientName || "Client documents"} />
      <main className="uploader-roi-content">
        <section className="roi-upload-intro">
          <div className="roi-upload-intro-icon"><CloudUploadOutlinedIcon /></div>
          <div><h3>Add scanned documents</h3><p>Upload up to 5 files to keep this client’s ROI documents together.</p></div>
        </section>
        <label className="roi-upload-dropzone" htmlFor="uploadFiles">
          <input type="file" id="uploadFiles" multiple onChange={handleFileChange} accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar,.7z" />
          <CloudUploadOutlinedIcon className="roi-dropzone-icon" />
          <span className="roi-dropzone-title">Drop files here or browse</span>
          <span className="roi-dropzone-hint">Images, PDF and office files · 100 MB maximum per file</span>
          <span className="roi-browse-button">Select files</span>
        </label>
        {files.length > 0 && <section className="roi-section"><div className="roi-section-heading"><div><span className="roi-section-kicker">Ready to upload</span><h3>Selected files</h3></div><span className="roi-count-badge">{files.length}/5</span></div><div className="roi-file-list">{files.map((file) => { const fileKey = getFileKey(file); const progress = uploadProgress[fileKey] || 0; const status = uploadStatus[fileKey] || "queued"; return <div className="roi-file-card" key={fileKey}><div className="roi-file-icon"><DescriptionOutlinedIcon /></div><div className="roi-file-details"><div className="roi-file-name" title={file.name}>{file.name}</div><div className="roi-file-meta">{formatFileSize(file.size)} · {status === "complete" ? "Uploaded" : status === "error" ? "Upload failed" : status === "uploading" ? `Uploading ${progress}%` : "Waiting"}</div><div className="roi-progress-track"><span style={{ width: `${progress}%` }} /></div></div>{status === "complete" ? <CheckCircleOutlineIcon className="roi-status-complete" /> : status === "error" ? <ErrorOutlineIcon className="roi-status-error" /> : <span className="roi-progress-value">{progress}%</span>}</div>; })}</div></section>}
        <section className="roi-section roi-existing-section"><div className="roi-section-heading"><div><span className="roi-section-kicker">Client library</span><h3>Uploaded documents</h3></div><span className="roi-count-badge">{fileLinks.length}</span></div>{fileLinks.length ? fileLinks.map((fileLink, index) => <div key={`${fileLink.url}-${index}`} className="roi-existing-file"><div className="roi-file-icon"><DescriptionOutlinedIcon /></div><a href={fileLink.url} target="_blank" rel="noreferrer" title={fileLink.name}>{fileLink.name}</a><DeleteButtonFile onClick={() => deleteFile(fileLink.url, fileLink.name, props.clientId)} fileName={fileLink.name} /></div>) : <div className="roi-empty-state">No documents uploaded yet.</div>}</section>
      </main>
      <footer className="roi-upload-footer"><Button className="roi-cancel-button" onClick={props.closemodal} disabled={isLoading}>Cancel</Button><Button className="roi-upload-button" onClick={() => updateClientData(props.clientId)} disabled={isLoading}>{isLoading ? "Uploading…" : "Upload files"}</Button></footer>
    </div>
  );
}

export default UpdloaderRoi;
